import { useFormikContext, FieldArray } from "formik";
import { Button, Form, Table } from "react-bootstrap"
import RequiredFormLabel from "../RequiredFormLabel/RequiredFormLabel";
import i18next from "i18next";
import { StellantisQuoteData } from "src/api/models/StellantisDTO";
import Select from 'react-select';
import * as Constants from "./stellantisConstants";
import ValidationError from "../ValidationError/ValidationError";
import MotornetVehicleSelector, { MotornetSelectedVehicle, getMakeName } from "../../motornet/common/MotornetVehicleSelector";
import { productionFetchParams } from "../../motornet/common/selectorMeta";
import * as Utils from './stellantisUtils';
import { FaPlus, FaTrash } from "react-icons/fa";
import { FileUploader } from "react-drag-drop-files";
import { useApiClient } from "src/hooks/useApiClient";
import { PropsWithChildren, useEffect, useState } from "react";
import { ReactSelectOption } from "src/types";
import DeleteOptionalModal from "./DeleteOptionalModal";
import _ from 'lodash';

type Props = PropsWithChildren<{}>;

const StellantisFormFields: React.FC<Props> = ({ children }) => {
  return <>{children}</>
}

type StellantisFieldProps = {
  disabled?: boolean;
  isCommercialVehicle?: boolean;
}

const TipoClienteFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue, touched, errors } = useFormikContext<StellantisQuoteData>()

  const onChange = (selected) => {
    const isAzienda = selected?.value === 'azienda';

    if (isAzienda) {
      setFieldValue('nome', '');
      setFieldValue('cognome', '');
    } else {
      setFieldValue('ragioneSociale', '');
    }

    setFieldValue('tipoCliente', selected ? selected.value : null);
  }

  const value = Constants.tipoClienteOptions?.find((option) => (values.tipoCliente ?? '') === option.value) || null;

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel
        label={i18next.t('client_type')}
        required={!values.tipoCliente}
      />
      <Select
        isDisabled={disabled}
        menuPosition="fixed"
        options={Constants.tipoClienteOptions}
        onChange={onChange}
        value={value}
        className={`custom-select ${touched.tipoCliente && errors.tipoCliente ? 'select-error' : ''}`}
      />
      <ValidationError {...{ errors: errors, touched: touched, name: 'tipoCliente' }} />
    </Form.Group>
  )
}

const NomeFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  const isRequired = ['libero_professionista', 'privato'].includes(values.tipoCliente) && !values.nome;

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel label={i18next.t('first_name')} required={!values.nome} />
      <Form.Control type="text"
        name='nome'
        disabled={disabled}
        required={isRequired}
        onChange={handleChange}
        value={values.nome}
      />
      <ValidationError {...{ errors, touched, name: 'nome' }} />
    </Form.Group>
  )
}

const CanoneFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>();

  const labelByClientType = values.tipoCliente === 'privato'
    ? i18next.t('canone_with_iva')
    : i18next.t('canone_without_iva');

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel label={labelByClientType} required={!values.canone} />
      <Form.Control
        min={0}
        step="any"
        type="number"
        placeholder={i18next.t('canone_input_placeholder')}
        name='canone'
        onWheel={(e) => e?.currentTarget?.blur()}
        disabled={disabled}
        required={!values.canone}
        onChange={handleChange}
        value={values.canone}
      />
      <ValidationError {...{ errors, touched, name: 'canone' }} />
    </Form.Group>
  )
}

const CognomeFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  const isRequired = ['libero_professionista', 'privato'].includes(values.tipoCliente) && !values.cognome;

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel
        label={i18next.t('last_name')}
        required={!values.cognome}
      />
      <Form.Control
        type="text"
        name="cognome"
        disabled={disabled}
        required={isRequired}
        onChange={handleChange}
        value={values.cognome ?? ''}
      />
      <ValidationError {...{ errors, touched, name: 'cognome' }} />
    </Form.Group>
  )
}

const RagioneSocialeFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  const isRequired = ['azienda'].includes(values.tipoCliente) && !values.ragioneSociale;

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel
        label={i18next.t('business_name')}
        required={isRequired}
      />
      <Form.Control
        type="text"
        disabled={disabled}
        name="ragioneSociale"
        onChange={handleChange}
        value={values.ragioneSociale ?? ''}
      />
      <ValidationError {...{ errors, touched, name: 'ragioneSociale' }} />
    </Form.Group>
  )
}

const KmAnnuiFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group className="">
      <RequiredFormLabel
        label={i18next.t('km_annui')}
        required={!values.kmAnnui}
      />
      <Form.Control
        disabled={disabled}
        onChange={handleChange}
        name="kmAnnui"
        value={values.kmAnnui}
        placeholder={i18next.t('kmAnnui_input_placeholder')}
        type="number"
        onWheel={(e) => e?.currentTarget?.blur()}
        id="kmAnnui"
      />
      <ValidationError {...{ errors, touched, name: 'kmAnnui' }} />

    </Form.Group>
  )
}

const DurataFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel
        label={i18next.t('duration') + ` (${i18next.t('months')})`.toLowerCase()}
        required={!values.durata}
      />
      <Form.Control
        disabled={disabled}
        onChange={handleChange}
        name="durata"
        value={values.durata}
        type="number"
        onWheel={(e) => e?.currentTarget?.blur()}
        placeholder={i18next.t('durata_input_placeholder')}
        id="durata"
      />
      <ValidationError {...{ errors, touched, name: 'durata' }} />
    </Form.Group>
  )
}

const AnticipoNoIvaFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel
        label={i18next.t('downpayment_noiva')}
        required={!values.anticipoNoIva}
      />
      <Form.Control
        disabled={disabled}
        onChange={handleChange}
        name="anticipoNoIva"
        value={values.anticipoNoIva}
        step="any"
        type="number"
        onWheel={(e) => e?.currentTarget?.blur()}
        placeholder={i18next.t('anticipoNoIva_input_placeholder')}
        id="anticipoNoIva"
      />
      <ValidationError {...{ errors, touched, name: 'anticipoNoIva' }} />
    </Form.Group>
  )
}

const RcaFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<StellantisQuoteData>()

  const onValueChange = (selected) => {
    setFieldValue('rcaPenaleRisarcitoria', selected ? selected.value : null);
  };

  const fieldValue = Constants.assicurazioneRCOptions?.find((option) => (values.rcaPenaleRisarcitoria ?? '') === option.value) || null;

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('liability_insurance')}
        required={Utils.isNilOrEmpty(values.rcaPenaleRisarcitoria)}
      />
      <Select
        isClearable={!disabled}
        isDisabled={disabled}
        menuPosition="fixed"
        options={Constants.assicurazioneRCOptions}
        onChange={onValueChange}
        value={fieldValue}
        className={`custom-select ${touched.rcaPenaleRisarcitoria && errors.rcaPenaleRisarcitoria ? 'select-error' : ''}`}
      />
      <ValidationError {...{ errors, touched, name: 'rcaPenaleRisarcitoria' }} />
    </>
  )
}

const KaskoFranchigiaFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<StellantisQuoteData>()

  const onValueChange = (selected) => {
    setFieldValue('kaskoFranchigia', selected ? selected.value : null);
  }

  const fieldValue = Constants.assicurazioneKaskoOptions?.find((option) => (values.kaskoFranchigia ?? '') === option.value) || null

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('kasko')}
        required={Utils.isNilOrEmpty(values.kaskoFranchigia)}
      />
      <Select
        isClearable={!disabled}
        isDisabled={disabled}
        menuPosition="fixed"
        options={Constants.assicurazioneKaskoOptions}
        onChange={onValueChange}
        value={fieldValue}
        className={`custom-select ${touched.kaskoFranchigia && errors.kaskoFranchigia ? 'select-error' : ''}`}
      />
      <ValidationError {...{ errors, touched, name: 'kaskoFranchigia' }} />
    </>
  )
}

const IncendioFurtoScopertoPercFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<StellantisQuoteData>()

  const onValueChange = (selected) => {
    setFieldValue('incendioFurtoScopertoPerc', selected ? selected.value : null);
  }

  const fieldValue = Constants.furtoIncendioOptions?.find((option) => (values.incendioFurtoScopertoPerc ?? '') === option.value) || null

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('theft_fire')}
        required={Utils.isNilOrEmpty(values.incendioFurtoScopertoPerc)}
      />
      <Select
        isClearable={!disabled}
        isDisabled={disabled}
        menuPosition="fixed"
        options={Constants.furtoIncendioOptions}
        onChange={onValueChange}
        value={fieldValue}
        className={`custom-select ${touched.incendioFurtoScopertoPerc && errors.incendioFurtoScopertoPerc ? 'select-error' : ''}`}
      />
      <ValidationError {...{ errors, touched, name: 'incendioFurtoScopertoPerc' }} />
    </>
  )
}

const GommeYNFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue } = useFormikContext<StellantisQuoteData>()

  const onValueChange = e => {
    const isChecked = e?.target.checked;
    if (!isChecked) {
      setFieldValue('tipoGomma', '');
      setFieldValue('numTrenoGommeEstive', '');
      setFieldValue('numTrenoGommeInvernali', '');
      setFieldValue('gommeIllimitateYN', false);
    }
    setFieldValue('gommeYN', isChecked)
  }

  return (
    <Form.Group className="d-flex">
      <RequiredFormLabel label={i18next.t('gomme_YN')} required={false} />
      <Form.Check
        disabled={disabled}
        id="gommeYN"
        name="gommeYN"
        type="switch"
        checked={values.gommeYN}
        onChange={onValueChange}
        className="hor-form-check-switch ms-2"
      />
    </Form.Group>
  )
}

const TipoGommaFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue, touched, errors } = useFormikContext<StellantisQuoteData>()

  const onValueChange = (selected) => {
    setFieldValue('tipoGomma', selected ? selected.value : null);
  }

  const fieldValue = Constants.tipoGommaOptions?.find((option) => (values.tipoGomma ?? '') === option.value) || null;

  return (
    <Form.Group>
      <RequiredFormLabel
        label={i18next.t('tipo_gomma')}
        required={!values.tipoGomma}
      />
      <Select
        isClearable={!disabled}
        isDisabled={disabled}
        menuPosition="fixed"
        options={Constants.tipoGommaOptions}
        onChange={onValueChange}
        value={fieldValue}
        className={`custom-select ${touched.tipoGomma && errors.tipoGomma ? 'select-error' : ''}`}
      />
      <ValidationError {...{ errors, touched, name: 'tipoGomma' }} />
    </Form.Group>
  )
}

const NumTrenoGommeEstiveFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group>
      <RequiredFormLabel
        label={i18next.t('n_treno_gomme_estive')}
        required={!values.gommeIllimitateYN && !values.numTrenoGommeEstive}
      />
      <Form.Control
        onChange={e => {
          e.preventDefault();
          const { value } = e.target;
          try {
            setFieldValue("numTrenoGommeEstive", Math.abs(parseInt(value)));
          } catch (error) {
            setFieldValue('numTrenoGommeEstive', '')
          }
        }}
        name="numTrenoGommeEstive"
        min={0}
        value={values.numTrenoGommeEstive}
        type="number"
        onWheel={(e) => e?.currentTarget?.blur()}
        placeholder={i18next.t('numTrenoGommeEstive_input_placeholder')}
        disabled={values.gommeIllimitateYN || disabled}
        id="numTrenoGommeEstive"
      />
      <ValidationError {...{ errors, touched, name: 'numTrenoGommeEstive' }} />
    </Form.Group>
  )
}

const NumTrenoGommeInvernaliFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group>
      <RequiredFormLabel
        label={i18next.t('n_treno_gomme_invernali')}
        required={!values.gommeIllimitateYN && !values.numTrenoGommeInvernali}
      />
      <Form.Control
        min={0}
        onChange={e => {
          e.preventDefault();
          const { value } = e.target;
          try {
            setFieldValue("numTrenoGommeInvernali", Math.abs(parseInt(value)));
          } catch (error) {
            setFieldValue('numTrenoGommeInvernali', '')
          }
        }}
        name="numTrenoGommeInvernali"
        value={values.numTrenoGommeInvernali}
        type="number"
        onWheel={(e) => e?.currentTarget?.blur()}
        placeholder={i18next.t('numTrenoGommeInvernali_input_placeholder')}
        disabled={values.gommeIllimitateYN || disabled}
        id="numTrenoGommeInvernali"
      />
      <ValidationError {...{ errors, touched, name: 'numTrenoGommeInvernali' }} />
    </Form.Group>
  )
}

const GommeIllimitateFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue } = useFormikContext<StellantisQuoteData>()

  const onValueChange = e => {
    const checked = e?.target?.checked;
    if (checked) {
      setFieldValue('numTrenoGommeInvernali', '');
      setFieldValue('numTrenoGommeEstive', '');
    }
    setFieldValue('gommeIllimitateYN', checked);
  }

  return (
    <Form.Group className="d-flex mt-4">
      <RequiredFormLabel label={i18next.t('gomme_illimitate_YN')} required={false} />
      <Form.Check
        id="gommeIllimitateYN"
        name="gommeIllimitateYN"
        className="ms-2"
        disabled={disabled}
        checked={values.gommeIllimitateYN}
        onChange={onValueChange}
      />
    </Form.Group>
  )
}

const AutoSostitutivaFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue } = useFormikContext<StellantisQuoteData>()

  const onValueChange = e => {
    const isChecked = e?.target.checked;

    if (!isChecked) {
      setFieldValue('autoSostitutivaGruppo', '')
    }

    setFieldValue('autoSostitutivaYN', isChecked)
  }

  return (
    <Form.Group className="d-flex mt-2">
      <RequiredFormLabel label={i18next.t('auto_sostitutiva_YN')} required={false} />
      <Form.Check
        id="autoSostitutivaYN"
        name="autoSostitutivaYN"
        type="switch"
        disabled={disabled}
        checked={values.autoSostitutivaYN}
        onChange={onValueChange}
        className="hor-form-check-switch ms-2"
      />
    </Form.Group>
  )
}

const RequestNoteFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, setFieldValue, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel required={false} label={i18next.t('notes')} />
      <Form.Control
        as="textarea"
        rows={3}
        disabled={disabled}
        name="requestNote"
        onChange={handleChange}
        value={values.requestNote || ''}
        placeholder={i18next.t('quote_request_additional_info')}
      />
      <ValidationError {...{ errors, touched, name: 'requestNote' }} />
    </Form.Group>
  )
}

const GruppoAutoSostitutivaFormField = ({ isCommercialVehicle, disabled = false }: StellantisFieldProps) => {
  const { setFieldValue, values, touched, errors } = useFormikContext<StellantisQuoteData>()

  const onValueChange = (selected) => {
    setFieldValue('autoSostitutivaGruppo', selected ? selected.value : null);
  }
  const options = isCommercialVehicle
    ? Constants.vetturaCommercialeSostitutivaOptions
    : Constants.vetturaSostitutivaOptions;

  const fieldValue = Constants.vetturaSostitutivaOptions.concat(Constants.vetturaCommercialeSostitutivaOptions)
    .find((option) => (values.autoSostitutivaGruppo ?? '') === option.value) || null;

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('auto_sostitutiva_gruppo')}
        required={!values.autoSostitutivaGruppo}
      />
      <Select
        isClearable={!disabled}
        isDisabled={disabled}
        menuPosition="fixed"
        options={options}
        onChange={onValueChange}
        value={fieldValue}
        className={`custom-select ${touched.autoSostitutivaGruppo && errors.autoSostitutivaGruppo ? 'select-error' : ''}`}
      />
      <ValidationError {...{ errors, touched, name: 'autoSostitutivaGruppo' }} />
    </>
  )
}

const RequestOptionalsFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, touched, errors, handleChange } = useFormikContext<StellantisQuoteData>()

  const removeItem = (idx: number, helper: any) => {
    if (values.optionals.length === 1) {
      helper.push({ descrizione: "" });
    }
    helper.remove(idx);
  }

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('optionals')}
        required={false}
      />
      <FieldArray name="optionals">
        {({ remove, push }) => (
          <>
            {values.optionals.map((o: any, i: number) => (
              <div key={i} className="mb-2 d-flex justify-content-between gap-4">
                <Form.Group className="flex-fill">
                  <Form.Control
                    value={values.optionals[i].descrizione}
                    type="text"
                    onChange={handleChange}
                    placeholder={i18next.t('insert_optionals')}
                    name={`optionals.${i}.descrizione`}
                  />
                  <ValidationError {...{ errors, touched, name: `optionals.${i}.descrizione` }} />
                </Form.Group>
                <div>
                  <Button
                    onClick={() => removeItem(i, { remove, push })}
                    variant="light"
                    className="btn btn-sm"
                  >
                    <FaTrash />
                  </Button>
                  {i === (values.optionals?.length - 1) && <Button
                    onClick={() => push({ descrizione: "" })}
                    variant="light"
                    className="btn btn-sm ms-2"
                  >
                    <FaPlus />
                  </Button>}
                </div>
              </div>
            ))}
          </>
        )}
      </FieldArray>
    </>
  )
}
const QuoteOptionalsFormField = () => {
  const [showDeleteOptional, setShowDeleteOptional] = useState(false);
  const { values, handleChange, errors, touched, setFieldValue } = useFormikContext<StellantisQuoteData>()
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const removeItem = (idx: number, helper: any) => {
    if (values.optionals.length === 1) {
      helper.push({ descrizione: "" });
    }
    helper.remove(idx);
  }

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('optionals')}
        required={false}
      />
      <FieldArray name="optionals">
        {({ remove, push }) => (
          <>
            <Table borderless>
              <thead>
                <tr>
                  <th>
                    <RequiredFormLabel
                      label={i18next.t('description')}
                    />
                  </th>
                  <th>
                    <RequiredFormLabel
                      label={i18next.t('price')}
                    />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {values.optionals.map((o: any, i: number) => (
                  <tr key={i}>
                    <td className="align-top">
                      <Form.Group>
                        <Form.Control
                          value={values.optionals[i].descrizione}
                          type="text"
                          disabled
                          onChange={handleChange}
                          name={`optionals.${i}.descrizione`}
                        />
                      </Form.Group>
                    </td>
                    <td className="align-top">
                      {values.optionals[i].unquotable
                        ? <Form.Group>
                          <Form.Control
                            className="text-muted"
                            value={i18next.t('optional_unquotable')}
                            type="text"
                            disabled
                          />
                        </Form.Group>
                        : <Form.Group>
                          <Form.Control
                            required
                            value={values.optionals[i].valore ?? ''}
                            type="number"
                            step="any"
                            onWheel={(e) => e?.currentTarget?.blur()}
                            placeholder={i18next.t('optionalPrice_input_placeholder')}
                            onChange={handleChange}
                            name={`optionals.${i}.valore`}
                          />
                          <ValidationError {...{ errors, touched, name: `optionals.${i}.valore` }} />
                        </Form.Group>
                      }
                    </td>
                    <td className="align-top">
                      {!values.optionals[i].unquotable && <Button
                        onClick={() => {
                          setSelectedIdx(i);
                          setShowDeleteOptional(true)
                        }}
                        variant="light"
                        className="btn btn-sm"
                      >
                        <FaTrash />
                      </Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <DeleteOptionalModal
              isOpen={showDeleteOptional}
              doDelete={() => {
                setShowDeleteOptional(false);
                const idx = selectedIdx;
                if (_.isNil(idx)) return;
                setFieldValue(`optionals.${idx}.unquotable`, true)
                setSelectedIdx(null)
              }}
              closeModal={() => {
                setShowDeleteOptional(false)
                setSelectedIdx(null);
              }}
            />
          </>
        )}
      </FieldArray>
    </>
  )
}

const MotornetVehicleFormField = ({ selectedVehicle, setSelectedVehicle, onCommercialChange }: any) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<StellantisQuoteData>()
  const { apiClient } = useApiClient();

  // On version selection, fetch the full Motornet vehicle once and denormalize the
  // specs onto the quote. We do NOT persist any vehicle identity (eurotax/jato id) —
  // the edit modal reads these stored specs back directly.
  const applyVehicleSpecs = async (version: any) => {
    const eurotax = version?.data?.codiceEurotax;
    if (!eurotax) return;
    try {
      const vehicleKind = selectedVehicle?.brand?.data?.vehicleKind ?? 'auto';
      const payload = await apiClient.motornetControllerService.getMotornetVehicle(eurotax, vehicleKind, productionFetchParams(version?.data, selectedVehicle?.model?.data));
      const m: any = payload?.details?.modello;
      setFieldValue('alimentazione', m?.alimentazione?.descrizione ?? null);
      setFieldValue('cambio', m?.cambio?.descrizione ?? m?.nomeCambio ?? null);
      setFieldValue('cavalli', m?.cavalliTotale ?? m?.hp ?? null);
      setFieldValue('kw', m?.kw ?? m?.potenzaTotale ?? null);
      setFieldValue('cilindrata', m?.cilindrata ?? null);
      setFieldValue('porte', m?.porte ?? null);
      setFieldValue('posti', m?.posti ?? null);
      setFieldValue('images', (payload?.images ?? []).map((img: any) => img?.url).filter(Boolean));
      // vehicleKind is UI-only (drives the auto-sostitutiva group options); not persisted.
      onCommercialChange?.(payload?.vehicleKind === 'vcom');
    } catch (error) {
      console.warn('Failed to fetch motornet vehicle details', error);
    }
  }

  return (
    <>
      <MotornetVehicleSelector
        showSelectLabels
        selectedVehicle={selectedVehicle}
        requiredBrand={!values.marca}
        requiredModel={!values.modello}
        requiredVersion={!values.allestimento}
        onBrandChange={(brand: any) => {
          setSelectedVehicle({ brand, model: null, version: null });
          setFieldValue('marca', getMakeName(brand));
          setFieldValue('modello', '');
          setFieldValue('allestimento', '');
          onCommercialChange?.(false);
        }}
        onModelChange={(model: any) => {
          setSelectedVehicle((prev: MotornetSelectedVehicle) => ({ ...prev, model, version: null }));
          const modelName = model?.data?.gammaModello?.descrizione
            ? _.startCase(_.toLower(model.data.gammaModello.descrizione))
            : (model?.label ?? '');
          setFieldValue('modello', modelName);
          setFieldValue('allestimento', '');
          onCommercialChange?.(false);
        }}
        onVersionChange={(version: any) => {
          setSelectedVehicle((prev: MotornetSelectedVehicle) => ({ ...prev, version }));
          setFieldValue('allestimento', version?.label ?? '');
          if (version?.data?.codiceEurotax) {
            applyVehicleSpecs(version);
          } else {
            onCommercialChange?.(false);
          }
        }}
      />
      <ValidationError {...{ errors, touched, name: 'modello' }} />
    </>
  )
}

const ExternalPdfFormField = ({ externalPdf, setExternalPdf }) => {

  return (
    <>
      <RequiredFormLabel label={i18next.t('external_pdf')} required={!externalPdf.file} />
      <FileUploader
        classes="dropzone text-center"
        fileOrFiles={externalPdf.file}
        types={['pdf']}
        handleChange={(file) => {
          setExternalPdf({ file })
        }}
        onTypeError={() => {
          setExternalPdf({ file: null, error: i18next.t('unsupported_file') })
        }}
      >
        <div className="fs-4">
          <div>{i18next.t('drag_file_here_or_click_to_upload_quote_file')}</div>
          <div className="fs-6 text-muted">
            {i18next.t('file_type')}: PDF
          </div>
        </div>
        {externalPdf.file ? <p className="mt-4">{(externalPdf.file as any).name}</p> : ''}
      </FileUploader>
      {
        externalPdf.file ? (
          <div className="d-flex justify-content-end text-right">
            <Button
              variant="danger"
              onClick={() => {
                setExternalPdf({ file: null })
              }}
              className="my-3 btn btn-sm btn-outline file-uploader-clear-btn"
              type="button"
            >
              {i18next.t('delete')}
            </Button>
          </div>
        ) : ('')
      }
    </>
  )
}

const NoleggiatoreFormField = ({ options, disabled = false }: { disabled?: boolean; options: ReactSelectOption[] }) => {
  const { values, setFieldValue, errors, touched, setFieldTouched, submitCount } = useFormikContext<StellantisQuoteData>()

  useEffect(() => {
    setFieldTouched('noleggiatore', true)
  }, [submitCount])

  const onValueChange = (selected) => {
    setFieldValue('noleggiatore', selected ? selected.value : null);
  };

  const fieldValue = options.find((option) => (values.noleggiatore ?? '') === option.value) || null;

  return (
    <>
      <RequiredFormLabel
        label={i18next.t('rental_company')}
        required={Utils.isNilOrEmpty(values.noleggiatore)}
      />
      <Select
        isClearable={!disabled}
        isDisabled={disabled}
        menuPosition="fixed"
        noOptionsMessage={() => `${i18next.t('loading')}...`}
        options={options as any}
        onChange={onValueChange}
        value={fieldValue}
        className={`custom-select ${touched.noleggiatore && errors.noleggiatore ? 'select-error' : ''}`}
        onBlur={() => {
          setFieldTouched("noleggiatore")
        }}
      />
      <ValidationError {...{ errors, touched, name: 'noleggiatore' }} />
    </>
  )
}

const ProvvigioniFormField = ({ disabled = false }: StellantisFieldProps) => {
  const { values, handleChange, errors, touched } = useFormikContext<StellantisQuoteData>()

  return (
    <Form.Group className="mb-3">
      <RequiredFormLabel
        label={i18next.t('provvigioni')}
        required={false}
      />
      <Form.Control
        disabled={disabled}
        onChange={handleChange}
        name="provvigioni"
        value={values.provvigioni}
        type="number"
        step="any"
        onWheel={(e) => e?.currentTarget?.blur()}
        placeholder={i18next.t('provvigioni_input_placeholder')}
        id="provvigioni"
      />
      <ValidationError {...{ errors, touched, name: 'provvigioni' }} />
    </Form.Group>
  )
}


export default Object.assign(StellantisFormFields, {
  TipoCliente: TipoClienteFormField,
  Nome: NomeFormField,
  Cognome: CognomeFormField,
  RagioneSociale: RagioneSocialeFormField,
  KmAnnui: KmAnnuiFormField,
  Durata: DurataFormField,
  AnticipoNoIva: AnticipoNoIvaFormField,
  Rca: RcaFormField,
  KaskoFranchigia: KaskoFranchigiaFormField,
  IncendioFurtoScopertoPerc: IncendioFurtoScopertoPercFormField,
  GommeYN: GommeYNFormField,
  TipoGomma: TipoGommaFormField,
  NumTrenoGommeEstive: NumTrenoGommeEstiveFormField,
  NumTrenoGommeInvernali: NumTrenoGommeInvernaliFormField,
  GommeIllimitate: GommeIllimitateFormField,
  AutoSostitutiva: AutoSostitutivaFormField,
  RequestNote: RequestNoteFormField,
  GruppoAutoSostitutiva: GruppoAutoSostitutivaFormField,
  RequestOptionals: RequestOptionalsFormField,
  MotornetVehicle: MotornetVehicleFormField,
  Canone: CanoneFormField,
  ExternalPdf: ExternalPdfFormField,
  QuoteOptionals: QuoteOptionalsFormField,
  Noleggiatore: NoleggiatoreFormField,
  Provvigioni: ProvvigioniFormField,
})
